import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../../api/axiosInstance';

const OnlineBookingSetting = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [settingData, setSettingData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [testingSync, setTestingSync] = useState(false);

    // Get doctor ID to verify super admin access
    const getUserId = () => {
        try {
            const userMedical = localStorage.getItem("userMedical");
            if (userMedical) {
                const userData = JSON.parse(userMedical);
                return userData?.id || userData?.data?.id || null;
            }
            return null;
        } catch (error) {
            return null;
        }
    };
    const doctorId = getUserId();
    const isSuperAdmin = String(doctorId) === '12';

    const defaultFormData = {
        wp_sync_enabled: 0,
        wp_db_host: '',
        wp_db_name: '',
        wp_db_user: '',
        wp_db_pass: '',
        auto_confirm_enabled: 0
    };

    const [formData, setFormData] = useState(defaultFormData);

    const fetchSettings = async () => {
        if (!isSuperAdmin) return;
        
        try {
            setLoading(true);
            const response = await axiosInstance.get('/online-appointment-settings/config');

            if (response.status === 200 && response.data?.data) {
                const data = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                setSettingData(data);
                
                setFormData({
                    wp_sync_enabled: data.wp_sync_enabled || 0,
                    wp_db_host: data.wp_db_host || '',
                    wp_db_name: data.wp_db_name || '',
                    wp_db_user: data.wp_db_user || '',
                    wp_db_pass: data.wp_db_pass || '',
                    auto_confirm_enabled: data.auto_confirm_enabled || 0
                });
            } else {
                setSettingData({});
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch online booking settings", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, [isSuperAdmin]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (settingData && Object.keys(settingData).length > 0) {
            setFormData({
                wp_sync_enabled: settingData.wp_sync_enabled || 0,
                wp_db_host: settingData.wp_db_host || '',
                wp_db_name: settingData.wp_db_name || '',
                wp_db_user: settingData.wp_db_user || '',
                wp_db_pass: settingData.wp_db_pass || '',
                auto_confirm_enabled: settingData.auto_confirm_enabled || 0
            });
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            
            // Only send fields relevant to the new schema
            const payload = {
                wp_sync_enabled: formData.wp_sync_enabled,
                wp_db_host: formData.wp_db_host,
                wp_db_name: formData.wp_db_name,
                wp_db_user: formData.wp_db_user,
                wp_db_pass: formData.wp_db_pass,
                auto_confirm_enabled: formData.auto_confirm_enabled
            };
            
            const response = await axiosInstance.put('/online-appointment-settings/config', payload);

            if (response.status === 200) {
                Swal.fire("Success", "Online Booking Settings updated successfully", "success");
                setIsEditing(false);
                fetchSettings();
            } else {
                Swal.fire("Error", "Failed to update settings", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong during update", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleTestSync = async () => {
        try {
            setTestingSync(true);
            const response = await axiosInstance.post('/online-appointment-settings/config/test-sync');
            if (response.status === 200 && response.data?.success) {
                Swal.fire("Success", "Database Connected Successfully", "success");
            } else {
                Swal.fire("Error", response.data?.message || "Test sync failed", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", error.response?.data?.message || "Error testing sync", "error");
        } finally {
            setTestingSync(false);
        }
    };

    if (!isSuperAdmin) {
        return (
            <div className="pc-container">
                <div className="pc-content">
                    <div className="card text-center p-5">
                        <h5 className="text-danger">Access Denied</h5>
                        <p>You do not have permission to view this page. Super admin access is required.</p>
                        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pc-container">
            <div className="pc-content">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" />
                    </div>
                ) : (
                    <>
                        <h5>
                            <i className="fa fa-arrow-left me-2" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} />
                            Online Booking Setting
                        </h5>

                        {settingData || isEditing ? (
                            <div className="card p-4">
                                {!isEditing ? (
                                    <>
                                        <div className="d-flex justify-content-end mb-3 gap-2">
                                            <button className="btn btn-info btn-sm" onClick={handleTestSync} disabled={testingSync}>
                                                <i className={`fa ${testingSync ? 'fa-spinner fa-spin' : 'fa-sync'} me-2`}></i>
                                                {testingSync ? 'Testing...' : 'Test Connection'}
                                            </button>
                                            <button className="btn btn-primary btn-sm" onClick={handleEdit}>
                                                <i className="fa fa-edit me-2"></i>Edit
                                            </button>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-12">
                                                <h6 className="text-primary mt-3 mb-2">WordPress Database Configuration</h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p><b>WP Sync Enabled:</b> {settingData?.wp_sync_enabled == 1 ? "Yes" : "No"}</p>
                                                        <p><b>DB Host:</b> {settingData?.wp_db_host}</p>
                                                        <p><b>DB Name:</b> {settingData?.wp_db_name}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p><b>DB User:</b> {settingData?.wp_db_user}</p>
                                                        <p><b>DB Password:</b> {settingData?.wp_db_pass ? '••••••••' : 'Not Set'}</p>
                                                        <p><b>Auto Confirm:</b> {settingData?.auto_confirm_enabled == 1 ? "Yes" : "No"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <form onSubmit={handleUpdate}>
                                        <h6 className="text-primary mb-3">WordPress Database Configuration</h6>
                                        <div className="row">
                                            <div className="col-md-6 mb-3 form-check px-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input ms-2"
                                                    id="wpSyncCheck"
                                                    name="wp_sync_enabled"
                                                    checked={formData.wp_sync_enabled == 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label ms-4" htmlFor="wpSyncCheck">WP Sync Enabled</label>
                                            </div>
                                            <div className="col-md-6 mb-3 form-check px-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input ms-2"
                                                    id="autoConfirmCheck"
                                                    name="auto_confirm_enabled"
                                                    checked={formData.auto_confirm_enabled == 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label ms-4" htmlFor="autoConfirmCheck">Auto Confirm Enabled</label>
                                            </div>
                                            
                                            <div className="col-md-12 mb-3">
                                                <label className="form-label">DB Host</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="wp_db_host"
                                                    value={formData.wp_db_host}
                                                    onChange={handleChange}
                                                    placeholder="Example: localhost or 124.x.x.x"
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">DB Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="wp_db_name"
                                                    value={formData.wp_db_name}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">DB User</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="wp_db_user"
                                                    value={formData.wp_db_user}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">DB Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    name="wp_db_pass"
                                                    value={formData.wp_db_pass}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            <button type="submit" className="btn btn-primary me-2">Update</button>
                                            <button type="button" className="cancelText" style={{ background: 'none', border: 'none', color: '#ff4d4f' }} onClick={handleCancel}>Cancel</button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        ) : (
                            <div className="card text-center p-5">
                                <h5>No Online Booking Setting Found</h5>
                                <button className="btn btn-primary mt-3 mx-auto" style={{ width: 'fit-content' }} onClick={handleEdit}>Create Settings</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default OnlineBookingSetting;
